// ============================================
// TYPES
// ============================================

export interface DynamicQuestion {
    id: string;
    question: string;
    placeholder: string;
    type: "text" | "textarea" | "number" | "select";
    options?: { value: string; label: string }[];
    suggestions?: string[];
    priority: "high" | "medium" | "low";
    category: string;
    showWhen: () => boolean;
  }
  
  export interface SelectedCondition {
    id: string;
    label: string;
    type: "common" | "icd10";
    code?: string;
  }
  
  export interface SelectedTreatment {
    id: string;
    label: string;
  }
  
  // ============================================
  // DYNAMIC QUESTIONS GENERATOR
  // ============================================
  
  export function generateDynamicQuestions(
    formData: Record<string, string>,
    selectedConditions: SelectedCondition[],
    selectedTreatments: SelectedTreatment[]
  ): DynamicQuestion[] {
    const questions: DynamicQuestion[] = [];
    const conditionLabels = (selectedConditions || []).map(c => c.label.toLowerCase()).join(" ");
    const treatmentLabels = (selectedTreatments || []).map(t => t.label.toLowerCase()).join(" ");
    const procedureName = formData.procedureName?.toLowerCase() || "";
  
    // ==========================================
    // MEDICATION-SPECIFIC QUESTIONS
    // ==========================================
    if (formData.requestType === "Medication") {
      // Step therapy questions
      if (formData.denialReason === "Step therapy required" || formData.formularyStatus === "step_therapy") {
        questions.push({
          id: "step_therapy_drugs",
          question: "Which required step therapy medications has the patient tried?",
          placeholder: "e.g., Metformin 2000mg daily x 6 months, Glipizide 10mg BID x 3 months",
          type: "textarea",
          priority: "high",
          category: "Step Therapy",
          suggestions: [
            "Metformin - max dose",
            "Sulfonylurea tried",
            "Generic tried first",
            "Tier 1 alternative tried",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "step_therapy_failures",
          question: "Why did each step therapy medication fail?",
          placeholder: "e.g., GI intolerance, inadequate efficacy after 3+ months, hypoglycemia",
          type: "textarea",
          priority: "high",
          category: "Step Therapy",
          suggestions: [
            "GI intolerance",
            "Inadequate response after 3+ months",
            "Allergic reaction",
            "Contraindicated due to renal function",
            "Hypoglycemia episodes",
          ],
          showWhen: () => true,
        });
      }
  
      // GLP-1 specific
      if (formData.medicationClass?.includes("GLP-1")) {
        questions.push({
          id: "glp1_a1c",
          question: "What is the patient's current A1c?",
          placeholder: "e.g., 8.5%",
          type: "text",
          priority: "high",
          category: "Clinical Values",
          suggestions: ["A1c >8%", "A1c >9%", "A1c at goal but weight concern"],
          showWhen: () => true,
        });
  
        questions.push({
          id: "glp1_weight",
          question: "What is the patient's BMI and weight history?",
          placeholder: "e.g., BMI 34, gained 15 lbs on sulfonylurea",
          type: "text",
          priority: "medium",
          category: "Clinical Values",
          suggestions: ["BMI >30", "BMI >35 with comorbidities", "Weight gain on current regimen"],
          showWhen: () => true,
        });
      }
  
      // Biologic specific
      if (formData.medicationClass?.includes("Biologic")) {
        questions.push({
          id: "biologic_dmards",
          question: "Which conventional DMARDs has the patient tried?",
          placeholder: "e.g., Methotrexate 25mg weekly x 6 months - inadequate response, elevated LFTs",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "Methotrexate - max tolerated dose",
            "Sulfasalazine trial",
            "Leflunomide trial",
            "Hydroxychloroquine trial",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "biologic_disease_activity",
          question: "What is the current disease activity score?",
          placeholder: "e.g., DAS28 of 5.2, CDAI 24, 6 swollen joints",
          type: "text",
          priority: "high",
          category: "Clinical Values",
          suggestions: ["DAS28 >3.2", "CDAI >10", "Multiple swollen/tender joints"],
          showWhen: () => true,
        });
      }
    }
  
    // ==========================================
    // IMAGING-SPECIFIC QUESTIONS
    // ==========================================
    if (formData.requestType === "Imaging") {
      // Prior imaging
      if (!treatmentLabels.includes("x-ray") && !treatmentLabels.includes("ultrasound")) {
        questions.push({
          id: "prior_imaging",
          question: "What prior imaging has been done and what did it show?",
          placeholder: "e.g., X-ray showed moderate DJD, US inconclusive for rotator cuff tear",
          type: "textarea",
          priority: "high",
          category: "Prior Workup",
          suggestions: [
            "X-ray completed - showed...",
            "X-ray negative/normal",
            "Ultrasound inconclusive",
            "Prior imaging insufficient for diagnosis",
          ],
          showWhen: () => true,
        });
      }
  
      // Red flags for advanced imaging
      if (formData.imagingType === "MRI" || formData.imagingType === "CT") {
        questions.push({
          id: "red_flag_symptoms",
          question: "Are there any red flag symptoms present?",
          placeholder: "e.g., Progressive weakness, bowel/bladder changes, unexplained weight loss",
          type: "textarea",
          priority: "high",
          category: "Clinical Urgency",
          suggestions: [
            "Progressive neurological deficit",
            "Bowel/bladder dysfunction",
            "Night pain waking from sleep",
            "Unexplained weight loss",
            "History of cancer",
            "Fever/infection concern",
          ],
          showWhen: () => true,
        });
      }
  
      // MSK imaging conservative treatment
      if (formData.bodyRegion?.toLowerCase().includes("spine") ||
          formData.bodyRegion?.includes("Knee") ||
          formData.bodyRegion?.includes("Shoulder") ||
          formData.bodyRegion?.includes("Hip")) {
        questions.push({
          id: "conservative_duration",
          question: "How long has conservative treatment been tried?",
          placeholder: "e.g., 8 weeks of PT (2x/week), NSAIDs daily x 6 weeks",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "PT x 6+ weeks",
            "PT x 12+ weeks",
            "NSAIDs x 4+ weeks",
            "Activity modification attempted",
            "Home exercise program",
          ],
          showWhen: () => !treatmentLabels.includes("physical therapy") && !treatmentLabels.includes("pt"),
        });
      }
  
      // Pelvic imaging for gynecological conditions
      if (formData.bodyRegion?.includes("Pelvis") || 
          conditionLabels.includes("fibroid") || 
          conditionLabels.includes("endometri") ||
          conditionLabels.includes("ovarian") ||
          conditionLabels.includes("uterine")) {
        questions.push({
          id: "pelvic_symptoms",
          question: "What are the primary gynecological symptoms?",
          placeholder: "e.g., Heavy menstrual bleeding, pelvic pain, bulk symptoms (urinary frequency, constipation)",
          type: "textarea",
          priority: "high",
          category: "Symptoms",
          suggestions: [
            "Heavy menstrual bleeding",
            "Pelvic pain/pressure",
            "Urinary frequency/urgency",
            "Dyspareunia",
            "Infertility concerns",
          ],
          showWhen: () => true,
        });
      }
    }
  
    // ==========================================
    // PROCEDURE-SPECIFIC QUESTIONS
    // ==========================================
    if (formData.requestType === "Procedure") {
      
      // ------------------------------------------
      // GYNECOLOGICAL PROCEDURES
      // ------------------------------------------
      const isGynProcedure = 
        procedureName.includes("hysterectomy") ||
        procedureName.includes("myomectomy") ||
        procedureName.includes("ablation") ||
        procedureName.includes("d&c") ||
        procedureName.includes("dilation") ||
        procedureName.includes("curettage") ||
        procedureName.includes("oophorectomy") ||
        procedureName.includes("salpingectomy") ||
        procedureName.includes("uterine") ||
        conditionLabels.includes("fibroid") ||
        conditionLabels.includes("endometri") ||
        conditionLabels.includes("adenomyosis") ||
        conditionLabels.includes("menorrhagia") ||
        conditionLabels.includes("uterine");
  
      if (isGynProcedure) {
        questions.push({
          id: "bleeding_quantification",
          question: "How would you quantify the patient's bleeding?",
          placeholder: "e.g., Soaking through 1 pad/hour, passing clots >1 inch, bleeding >7 days per cycle",
          type: "textarea",
          priority: "high",
          category: "Symptom Severity",
          suggestions: [
            "Soaking >1 pad/hour",
            "Clots larger than quarter",
            "Periods lasting >7 days",
            "Flooding/accidents",
            "Requires double protection",
            "Changing pads overnight",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "anemia_status",
          question: "What is the patient's hemoglobin/anemia status?",
          placeholder: "e.g., Hemoglobin 9.2 g/dL, iron deficiency anemia requiring IV iron",
          type: "text",
          priority: "high",
          category: "Clinical Values",
          suggestions: [
            "Hemoglobin <10 g/dL",
            "Hemoglobin <8 g/dL",
            "Iron deficiency anemia",
            "Required transfusion",
            "On iron supplementation",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "gyn_symptoms",
          question: "What other symptoms is the patient experiencing?",
          placeholder: "e.g., Severe dysmenorrhea, pelvic pressure, urinary frequency, constipation",
          type: "textarea",
          priority: "high",
          category: "Symptoms",
          suggestions: [
            "Severe pelvic pain",
            "Dysmenorrhea limiting activities",
            "Pelvic pressure/bloating",
            "Urinary frequency/retention",
            "Bowel symptoms",
            "Dyspareunia",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "gyn_prior_treatment",
          question: "What medical/conservative treatments have been tried?",
          placeholder: "e.g., OCPs x 6 months, Mirena IUD x 1 year, Tranexamic acid, NSAIDs",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "Combined oral contraceptives",
            "Progestin-only therapy",
            "Mirena/hormonal IUD",
            "Tranexamic acid (Lysteda)",
            "GnRH agonist (Lupron)",
            "NSAIDs for pain",
            "Iron supplementation",
          ],
          showWhen: () => !treatmentLabels.includes("contraceptive") && !treatmentLabels.includes("iud") && !treatmentLabels.includes("hormonal"),
        });
  
        questions.push({
          id: "fibroid_characteristics",
          question: "What are the fibroid characteristics on imaging?",
          placeholder: "e.g., 8cm intramural fibroid, multiple fibroids with largest 6cm, submucosal fibroid distorting cavity",
          type: "textarea",
          priority: "medium",
          category: "Imaging Findings",
          suggestions: [
            "Submucosal (distorts cavity)",
            "Large fibroid >5cm",
            "Multiple fibroids",
            "Rapid growth on serial imaging",
            "Degenerating fibroid",
          ],
          showWhen: () => conditionLabels.includes("fibroid") || procedureName.includes("myomectomy"),
        });
  
        questions.push({
          id: "fertility_status",
          question: "What is the patient's fertility status/desire?",
          placeholder: "e.g., Completed childbearing, desires future fertility, age 45 with no future pregnancy plans",
          type: "text",
          priority: "medium",
          category: "Patient Factors",
          suggestions: [
            "Completed childbearing",
            "Desires future fertility",
            "No future pregnancy plans",
            "Infertility due to fibroids",
          ],
          showWhen: () => true,
        });
      }
  
      // ------------------------------------------
      // ORTHOPEDIC / SPINE SURGERY
      // ------------------------------------------
      const isSpineSurgery = 
        procedureName.includes("fusion") ||
        procedureName.includes("laminectomy") ||
        procedureName.includes("discectomy") ||
        procedureName.includes("decompression") ||
        procedureName.includes("spine") ||
        procedureName.includes("lumbar") ||
        procedureName.includes("cervical") ||
        procedureName.includes("thoracic");
  
      if (isSpineSurgery) {
        questions.push({
          id: "neurological_symptoms",
          question: "What neurological symptoms are present?",
          placeholder: "e.g., L5 radiculopathy with foot drop, numbness in S1 distribution, bilateral leg weakness",
          type: "textarea",
          priority: "high",
          category: "Neurological Status",
          suggestions: [
            "Radicular pain in specific dermatome",
            "Motor weakness (specify grade)",
            "Sensory deficit/numbness",
            "Reflex changes",
            "Bowel/bladder dysfunction",
            "Gait instability",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "spine_imaging_findings",
          question: "What do the imaging studies show?",
          placeholder: "e.g., MRI shows L4-5 disc herniation with severe central stenosis, nerve root compression",
          type: "textarea",
          priority: "high",
          category: "Imaging Findings",
          suggestions: [
            "Disc herniation with nerve compression",
            "Severe central stenosis",
            "Foraminal stenosis",
            "Spondylolisthesis",
            "Cord compression/myelopathy",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "spine_conservative_duration",
          question: "What conservative treatments have been tried and for how long?",
          placeholder: "e.g., 12 weeks PT, 3 epidural steroid injections with temporary relief, NSAIDs/gabapentin",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "PT x 6+ weeks",
            "PT x 12+ weeks",
            "Epidural steroid injection x1",
            "Epidural steroid injection x3",
            "Oral steroids",
            "Gabapentin/Lyrica trial",
          ],
          showWhen: () => true,
        });
      }
  
      // ------------------------------------------
      // JOINT REPLACEMENT
      // ------------------------------------------
      const isJointReplacement = 
        procedureName.includes("replacement") ||
        procedureName.includes("arthroplasty") ||
        procedureName.includes("tkr") ||
        procedureName.includes("thr") ||
        procedureName.includes("tka") ||
        procedureName.includes("tha");
  
      if (isJointReplacement) {
        questions.push({
          id: "joint_imaging",
          question: "What do the X-rays show?",
          placeholder: "e.g., Bone-on-bone arthritis, Kellgren-Lawrence grade 4, complete joint space loss",
          type: "textarea",
          priority: "high",
          category: "Imaging Findings",
          suggestions: [
            "Bone-on-bone contact",
            "Kellgren-Lawrence grade 4",
            "Complete joint space loss",
            "Subchondral cysts",
            "Large osteophytes",
            "Joint deformity",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "joint_function",
          question: "How is joint function affected?",
          placeholder: "e.g., Walking distance <1 block, cannot climb stairs, requires assistive device",
          type: "textarea",
          priority: "high",
          category: "Functional Status",
          suggestions: [
            "Walking distance severely limited",
            "Cannot climb stairs",
            "Requires assistive device",
            "Unable to perform ADLs",
            "Night pain disrupting sleep",
            "Cannot work due to pain",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "joint_conservative",
          question: "What conservative measures have been exhausted?",
          placeholder: "e.g., PT, weight loss 20 lbs, NSAIDs, 3 cortisone injections, hyaluronic acid series",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "Physical therapy completed",
            "Weight loss achieved",
            "NSAIDs max duration",
            "Cortisone injections x3",
            "Hyaluronic acid injections",
            "Bracing trial",
          ],
          showWhen: () => true,
        });
      }
  
      // ------------------------------------------
      // BARIATRIC SURGERY
      // ------------------------------------------
      const isBariatric = 
        procedureName.includes("bariatric") ||
        procedureName.includes("gastric bypass") ||
        procedureName.includes("gastric sleeve") ||
        procedureName.includes("sleeve gastrectomy") ||
        procedureName.includes("roux-en-y") ||
        procedureName.includes("lap band") ||
        procedureName.includes("weight loss surgery");
  
      if (isBariatric) {
        questions.push({
          id: "bmi_history",
          question: "What is the patient's BMI and weight history?",
          placeholder: "e.g., Current BMI 42, has been >35 for 5+ years, highest weight 310 lbs",
          type: "textarea",
          priority: "high",
          category: "Clinical Values",
          suggestions: [
            "BMI >40",
            "BMI >35 with comorbidities",
            "BMI stable >5 years",
            "Weight documented over time",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "weight_loss_attempts",
          question: "What supervised weight loss attempts have been made?",
          placeholder: "e.g., 6-month physician-supervised program, dietitian visits, lost 10 lbs then regained",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "Physician-supervised diet x 6 months",
            "Registered dietitian program",
            "Commercial program (Weight Watchers, etc.)",
            "Medication trial (Wegovy, Qsymia, etc.)",
            "Documented weight loss and regain",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "obesity_comorbidities",
          question: "What obesity-related comorbidities are present?",
          placeholder: "e.g., Type 2 diabetes (A1c 8.5%), HTN on 3 medications, severe OSA on CPAP",
          type: "textarea",
          priority: "high",
          category: "Comorbidities",
          suggestions: [
            "Type 2 diabetes",
            "Hypertension",
            "Obstructive sleep apnea",
            "NAFLD/NASH",
            "Osteoarthritis",
            "GERD",
            "Hyperlipidemia",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "psych_clearance",
          question: "Has psychological evaluation been completed?",
          placeholder: "e.g., Psych eval completed 1/15/25, cleared for surgery, no active eating disorder",
          type: "text",
          priority: "medium",
          category: "Preoperative Requirements",
          suggestions: [
            "Psych eval completed and cleared",
            "No active eating disorder",
            "Stable mental health",
            "Completed preop education",
          ],
          showWhen: () => true,
        });
      }
  
      // ------------------------------------------
      // CARDIAC PROCEDURES
      // ------------------------------------------
      const isCardiacProcedure = 
        procedureName.includes("cabg") ||
        procedureName.includes("bypass") ||
        procedureName.includes("stent") ||
        procedureName.includes("pci") ||
        procedureName.includes("cath") ||
        procedureName.includes("valve") ||
        procedureName.includes("tavr") ||
        (procedureName.includes("ablation") && conditionLabels.includes("afib")) ||
        procedureName.includes("pacemaker") ||
        procedureName.includes("icd") ||
        procedureName.includes("defibrillator");
  
      if (isCardiacProcedure) {
        questions.push({
          id: "cardiac_symptoms",
          question: "What cardiac symptoms is the patient experiencing?",
          placeholder: "e.g., Angina with minimal exertion, NYHA Class III dyspnea, syncope x2",
          type: "textarea",
          priority: "high",
          category: "Symptoms",
          suggestions: [
            "Angina at rest",
            "Angina with minimal exertion",
            "NYHA Class III symptoms",
            "NYHA Class IV symptoms",
            "Syncope/presyncope",
            "Palpitations affecting daily life",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "cardiac_testing",
          question: "What cardiac testing has been done?",
          placeholder: "e.g., Stress test positive at 4 METs, cath showed 90% LAD stenosis, EF 35%",
          type: "textarea",
          priority: "high",
          category: "Diagnostic Workup",
          suggestions: [
            "Stress test positive",
            "Echo EF <40%",
            "Cath with significant stenosis",
            "Severe valve disease on echo",
            "Holter with significant arrhythmia",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "medical_optimization",
          question: "Has medical therapy been optimized?",
          placeholder: "e.g., On maximally tolerated GDMT including metoprolol, lisinopril, atorvastatin",
          type: "textarea",
          priority: "high",
          category: "Medical Management",
          suggestions: [
            "On optimal medical therapy",
            "Beta blocker maximized",
            "ACE/ARB maximized",
            "Antiplatelet therapy",
            "Statin therapy",
            "Medication limited by side effects",
          ],
          showWhen: () => true,
        });
      }
  
      // ------------------------------------------
      // GENERAL PROCEDURE QUESTIONS (fallback)
      // ------------------------------------------
      if (!isGynProcedure && !isSpineSurgery && !isJointReplacement && !isBariatric && !isCardiacProcedure) {
        questions.push({
          id: "functional_impact",
          question: "How is this condition impacting daily function?",
          placeholder: "e.g., Unable to work x 3 months, can't walk >1 block, sleep disrupted nightly",
          type: "textarea",
          priority: "high",
          category: "Medical Necessity",
          suggestions: [
            "Unable to work",
            "Cannot perform ADLs",
            "Sleep disrupted nightly",
            "Pain 7-10/10 daily",
            "Requires assistance with mobility",
            "Cannot sit/stand >30 minutes",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "pain_score",
          question: "What is the patient's pain level? (0-10)",
          placeholder: "e.g., 8/10 constant, 10/10 with activity",
          type: "text",
          priority: "medium",
          category: "Clinical Values",
          suggestions: ["7-8/10 average", "8-10/10 with activity", "Constant baseline pain"],
          showWhen: () => true,
        });
  
        questions.push({
          id: "injection_history",
          question: "Have therapeutic injections been tried?",
          placeholder: "e.g., 3 epidural steroid injections with <50% relief lasting <2 weeks",
          type: "textarea",
          priority: "high",
          category: "Prior Treatment",
          suggestions: [
            "Steroid injection x1",
            "Steroid injections x3",
            "Nerve block - diagnostic",
            "<50% relief from injections",
            "Relief lasted <2 weeks",
          ],
          showWhen: () => !treatmentLabels.includes("injection") && !treatmentLabels.includes("steroid"),
        });
      }
    }
  
    // ==========================================
    // DME-SPECIFIC QUESTIONS
    // ==========================================
    if (formData.requestType === "DME (Durable Medical Equipment)") {
      // CPAP/BiPAP
      if (formData.dmeCategory?.includes("CPAP") || formData.dmeCategory?.includes("BiPAP")) {
        questions.push({
          id: "ahi_score",
          question: "What was the AHI on sleep study?",
          placeholder: "e.g., AHI 32, with oxygen nadir of 78%",
          type: "text",
          priority: "high",
          category: "Clinical Values",
          suggestions: ["AHI >15", "AHI >30", "Oxygen nadir <88%", "Central apneas present"],
          showWhen: () => true,
        });
  
        questions.push({
          id: "osa_symptoms",
          question: "What symptoms does the patient have?",
          placeholder: "e.g., Excessive daytime sleepiness, witnessed apneas, morning headaches",
          type: "textarea",
          priority: "medium",
          category: "Symptoms",
          suggestions: [
            "Excessive daytime sleepiness",
            "Witnessed apneas",
            "Morning headaches",
            "Unrefreshing sleep",
            "Epworth score >10",
          ],
          showWhen: () => true,
        });
      }
  
      // Oxygen
      if (formData.dmeCategory?.includes("Oxygen")) {
        questions.push({
          id: "oxygen_values",
          question: "What are the oxygen saturation values?",
          placeholder: "e.g., Resting SpO2 86%, drops to 82% with ambulation",
          type: "text",
          priority: "high",
          category: "Clinical Values",
          suggestions: ["SpO2 <88% at rest", "SpO2 drops with exertion", "PaO2 <55 on ABG"],
          showWhen: () => true,
        });
      }
  
      // Wheelchair
      if (formData.dmeCategory?.includes("Wheelchair")) {
        questions.push({
          id: "mobility_status",
          question: "What is the patient's mobility status?",
          placeholder: "e.g., Cannot ambulate >10 feet, falls 3x/week, uses furniture for support",
          type: "textarea",
          priority: "high",
          category: "Functional Assessment",
          suggestions: [
            "Cannot ambulate >10 feet",
            "Requires 2-person assist",
            "Falls frequently",
            "Uses furniture/walls for support",
          ],
          showWhen: () => true,
        });
  
        questions.push({
          id: "home_assessment",
          question: "Has a home assessment been done?",
          placeholder: "e.g., Single-story home, 32-inch doorways, caregiver available",
          type: "text",
          priority: "medium",
          category: "Home Environment",
          suggestions: ["Single-story home", "Ramp available", "Doorways measured", "Caregiver present"],
          showWhen: () => true,
        });
      }
  
      // CGM
      if (formData.dmeCategory?.includes("Glucose") || formData.dmeCategory?.includes("CGM")) {
        questions.push({
          id: "hypoglycemia_history",
          question: "History of hypoglycemia or hypoglycemia unawareness?",
          placeholder: "e.g., 3 severe hypoglycemic episodes in past 6 months, hypoglycemia unawareness",
          type: "textarea",
          priority: "high",
          category: "Clinical History",
          suggestions: [
            "Hypoglycemia unawareness",
            "Severe hypoglycemia (required assistance)",
            "Frequent hypoglycemia despite adjustment",
            "Nocturnal hypoglycemia",
          ],
          showWhen: () => true,
        });
      }
    }
  
    // ==========================================
    // DENIAL-REASON SPECIFIC QUESTIONS
    // ==========================================
    if (formData.denialReason === "Not medically necessary") {
      questions.push({
        id: "severity_evidence",
        question: "What evidence demonstrates severity of the condition?",
        placeholder: "e.g., Labs, imaging findings, exam findings, validated scores",
        type: "textarea",
        priority: "high",
        category: "Severity Documentation",
        suggestions: [
          "Elevated inflammatory markers",
          "Imaging shows progression",
          "Validated score above threshold",
          "Objective exam findings",
        ],
        showWhen: () => !questions.some(q => q.id === "functional_impact"),
      });
  
      questions.push({
        id: "risk_if_denied",
        question: "What happens to this patient if treatment is denied?",
        placeholder: "e.g., Disease progression, hospitalization risk, permanent disability",
        type: "textarea",
        priority: "high",
        category: "Risk Assessment",
        suggestions: [
          "Disease progression likely",
          "Hospitalization risk",
          "ED visits anticipated",
          "Permanent disability risk",
          "Quality of life decline",
        ],
        showWhen: () => true,
      });
    }
  
    if (formData.denialReason === "Experimental/investigational") {
      questions.push({
        id: "fda_status",
        question: "What is the FDA approval status?",
        placeholder: "e.g., FDA approved for this indication in 2020",
        type: "text",
        priority: "high",
        category: "Regulatory Status",
        suggestions: [
          "FDA approved for this indication",
          "FDA approved, off-label use supported by guidelines",
          "Breakthrough therapy designation",
        ],
        showWhen: () => true,
      });
  
      questions.push({
        id: "guideline_support",
        question: "Which clinical guidelines support this treatment?",
        placeholder: "e.g., AHA 2023 Heart Failure Guidelines recommend...",
        type: "textarea",
        priority: "high",
        category: "Evidence Support",
        suggestions: [
          "Recommended in society guidelines",
          "Grade A recommendation",
          "Standard of care at academic centers",
          "Supported by RCT evidence",
        ],
        showWhen: () => true,
      });
    }
  
    if (formData.denialReason === "Does not meet clinical criteria") {
      questions.push({
        id: "criteria_points",
        question: "Which specific criteria does the patient meet?",
        placeholder: "e.g., BMI >40, or BMI >35 with HTN and T2DM, failed 6-month diet program",
        type: "textarea",
        priority: "high",
        category: "Criteria Checklist",
        suggestions: [
          "Meets primary diagnostic criteria",
          "Meets severity threshold",
          "Has required comorbidities",
          "Completed required conservative tx duration",
        ],
        showWhen: () => true,
      });
    }
  
    // ==========================================
    // CONDITION-SPECIFIC QUESTIONS
    // ==========================================
    
    // Diabetes
    if (conditionLabels.includes("diabet")) {
      if (!questions.some(q => q.id === "glp1_a1c")) {
        questions.push({
          id: "diabetes_control",
          question: "What is the current A1c and diabetes regimen?",
          placeholder: "e.g., A1c 8.9% on metformin 2000mg + glipizide 10mg BID",
          type: "text",
          priority: "medium",
          category: "Disease Status",
          suggestions: ["A1c >7%", "A1c >8%", "On multiple oral agents", "On insulin"],
          showWhen: () => true,
        });
      }
    }
  
    // Heart failure
    if (conditionLabels.includes("heart failure") || conditionLabels.includes("chf") || conditionLabels.includes("hfref") || conditionLabels.includes("hfpef")) {
      if (!questions.some(q => q.id === "ef_nyha") && !questions.some(q => q.id === "cardiac_symptoms")) {
        questions.push({
          id: "ef_nyha",
          question: "What is the EF and NYHA class?",
          placeholder: "e.g., EF 30%, NYHA Class III, 2 hospitalizations in 6 months",
          type: "text",
          priority: "high",
          category: "Disease Status",
          suggestions: ["EF <40%", "NYHA Class III", "NYHA Class IV", "Recent hospitalization"],
          showWhen: () => true,
        });
      }
    }
  
    // Chronic pain / spine
    if (conditionLabels.includes("pain") || conditionLabels.includes("back") || conditionLabels.includes("spine")) {
      if (!questions.some(q => q.id === "pain_score") && !questions.some(q => q.id === "neurological_symptoms")) {
        questions.push({
          id: "pain_details",
          question: "Describe pain severity and impact",
          placeholder: "e.g., 8/10 constant low back pain radiating to L leg, can't sit >20 min",
          type: "textarea",
          priority: "high",
          category: "Symptoms",
          suggestions: ["Pain 7-10/10", "Radicular symptoms", "Neurological deficits", "Sleep affected"],
          showWhen: () => true,
        });
      }
    }
  
    // Filter out questions whose showWhen returns false
    return questions.filter(q => q.showWhen()).slice(0, 8);
  }